import React from "react";
import { Helmet } from "react-helmet";
import { FaBookOpen, FaUsers, FaHeart, FaBrain, FaHandsHelping } from "react-icons/fa";

import YouthIssues from "../components/youthissues";
import PathwayBookCarousel from "../components/pathway_book_carousel";

export default function Curriculum() {
  return (
    <div className="font-nunito">
      <Helmet>
        <link rel="icon" href="favicon.ico" />
        <title>Youth Curriculum | Pathway Resource Center</title>
        <meta
          name="description"
          content="Learn about bible-based curriculum and lessons for teaching kids life skills and risk avoidance. Lessons cover topics like goal-setting, social media risks, abstinence and drug avoidance, and relationship skills."
        />
      </Helmet>

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[#457B9D] via-[#5a8fb0] to-[#457B9D] py-20">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute -left-20 -top-20 h-80 w-80 rounded-full bg-white blur-3xl"></div>
          <div className="absolute -right-32 bottom-0 h-96 w-96 rounded-full bg-white blur-3xl"></div>
        </div>

        <div className="relative mx-auto max-w-6xl px-6">
          <div className="text-center">
            <div className="mb-6 inline-block rounded-full bg-white/20 px-6 py-2 backdrop-blur-sm">
              <span className="text-sm font-semibold text-white">
                FOR EDUCATORS & YOUTH PROGRAMS
              </span>
            </div>
            <h1 className="mb-6 text-4xl font-bold text-white md:text-5xl lg:text-6xl">
              Pathway Life Skills &<br />Risk Avoidance Curriculum
            </h1>
            <p className="mx-auto mb-10 max-w-3xl text-xl leading-relaxed text-white/95 md:text-2xl">
              Equipping young people with Biblical wisdom and practical skills to make wise choices and avoid risky behaviors
            </p>
            <a
              href="/curriculum/order"
              className="group inline-flex items-center gap-2 rounded-full bg-white px-8 py-4 text-lg font-semibold text-[#457B9D] shadow-xl transition-all duration-300 hover:scale-105 hover:shadow-2xl"
            >
              <FaBookOpen className="text-xl" />
              Order Curriculum Now
              <svg
                className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M14 5l7 7m0 0l-7 7m7-7H3"
                />
              </svg>
            </a>
          </div>
        </div>

        {/* Wave Divider */}
        <div className="absolute bottom-0 left-0 w-full">
          <svg
            viewBox="0 0 1440 120"
            className="w-full"
            preserveAspectRatio="none"
            style={{ height: '80px' }}
          >
            <path
              d="M0,64 C360,120 720,0 1440,64 L1440,120 L0,120 Z"
              fill="white"
              opacity="0.3"
            />
            <path
              d="M0,80 C360,20 720,100 1440,80 L1440,120 L0,120 Z"
              fill="white"
            />
          </svg>
        </div>
      </section>

      {/* Mission Statement */}
      <section className="bg-gradient-to-b from-white to-blue-50 py-16">
        <div className="mx-auto max-w-5xl px-6">
          <div className="rounded-3xl bg-white p-8 shadow-xl md:p-12">
            <div className="mb-6 flex items-center justify-center">
              <div className="rounded-full bg-blue-100 p-4">
                <FaHeart className="text-3xl text-[#457B9D]" />
              </div>
            </div>
            <h2 className="mb-6 text-center text-2xl font-bold text-gray-800 md:text-3xl">
              Why Pathway Curriculum?
            </h2>
            <p className="text-center text-lg leading-relaxed text-gray-700 md:text-xl">
              Every day, young people encounter choices about life and relationships. Yet they often lack the information and skills needed to help them make wise decisions and avoid risky behavior. At Pathway, we want to{" "}
              <span className="font-semibold text-[#457B9D]">
                prevent the pain of unintended consequences
              </span>{" "}
              that come with uninformed choices. We take a proactive approach to real-world issues young people are facing through our comprehensive youth development program.
            </p>
          </div>
        </div>
      </section>

      {/* Curriculum Preview Section */}
      <section className="bg-gradient-to-b from-blue-50 to-white py-16">
        <div className="mx-auto max-w-6xl px-6">
          <div className="mb-12 text-center">
            <h2 className="mb-4 text-3xl font-bold text-gray-800 md:text-4xl">
              Preview the Curriculum
            </h2>
            <p className="text-lg text-gray-600">
              Explore our comprehensive 8-lesson workbook designed for real impact
            </p>
          </div>
          <PathwayBookCarousel />
        </div>
      </section>

      {/* Key Features Section */}
      <section className="bg-white py-16">
        <div className="mx-auto max-w-6xl px-6">
          <div className="mb-12 text-center">
            <h2 className="mb-4 text-3xl font-bold text-gray-800 md:text-4xl">
              Curriculum Features
            </h2>
            <div className="mx-auto h-1 w-24 rounded bg-[#457B9D]"></div>
          </div>

          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-4">
            <div className="group rounded-2xl bg-gradient-to-br from-blue-50 to-white p-6 shadow-md transition-all duration-300 hover:-translate-y-2 hover:shadow-xl">
              <div className="mb-4 inline-flex rounded-full bg-blue-100 p-4">
                <FaBookOpen className="text-2xl text-[#457B9D]" />
              </div>
              <h3 className="mb-3 text-xl font-bold text-gray-800">8 Comprehensive Lessons</h3>
              <p className="leading-relaxed text-gray-600">
                Structured curriculum covering essential life skills and risk avoidance topics
              </p>
            </div>

            <div className="group rounded-2xl bg-gradient-to-br from-blue-50 to-white p-6 shadow-md transition-all duration-300 hover:-translate-y-2 hover:shadow-xl">
              <div className="mb-4 inline-flex rounded-full bg-blue-100 p-4">
                <FaBrain className="text-2xl text-[#457B9D]" />
              </div>
              <h3 className="mb-3 text-xl font-bold text-gray-800">Biblically-Based</h3>
              <p className="leading-relaxed text-gray-600">
                Grounded in Biblical principles while addressing real-world challenges
              </p>
            </div>

            <div className="group rounded-2xl bg-gradient-to-br from-blue-50 to-white p-6 shadow-md transition-all duration-300 hover:-translate-y-2 hover:shadow-xl">
              <div className="mb-4 inline-flex rounded-full bg-blue-100 p-4">
                <FaUsers className="text-2xl text-[#457B9D]" />
              </div>
              <h3 className="mb-3 text-xl font-bold text-gray-800">Prevention-Focused</h3>
              <p className="leading-relaxed text-gray-600">
                Proactive education designed to prevent risky behaviors before they start
              </p>
            </div>

            <div className="group rounded-2xl bg-gradient-to-br from-blue-50 to-white p-6 shadow-md transition-all duration-300 hover:-translate-y-2 hover:shadow-xl">
              <div className="mb-4 inline-flex rounded-full bg-blue-100 p-4">
                <FaHandsHelping className="text-2xl text-[#457B9D]" />
              </div>
              <h3 className="mb-3 text-xl font-bold text-gray-800">Holistic Development</h3>
              <p className="leading-relaxed text-gray-600">
                Addresses physical, emotional, social, and spiritual well-being
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* What Students Learn */}
      <section className="bg-gradient-to-b from-white to-blue-50 py-16">
        <div className="mx-auto max-w-6xl px-6">
          <div className="mb-12 text-center">
            <h2 className="mb-4 text-3xl font-bold text-gray-800 md:text-4xl">
              Skills for Life Success
            </h2>
            <p className="text-lg text-gray-600">
              Young people will develop essential skills to navigate today's challenges
            </p>
          </div>

          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {[
              "Goal-Setting & Future Planning",
              "Healthy Relationship Skills",
              "Social Media & Digital Literacy",
              "Drug & Substance Avoidance",
              "Sexual Risk Avoidance",
              "Emotional Intelligence",
              "Decision-Making Skills",
              "Communication & Boundaries",
              "Self-Worth & Identity"
            ].map((skill, index) => (
              <div
                key={index}
                className="group flex items-start gap-3 rounded-xl bg-white p-5 shadow-md transition-all duration-300 hover:shadow-lg"
              >
                <div className="mt-1 flex-shrink-0">
                  <div className="flex h-6 w-6 items-center justify-center rounded-full bg-[#457B9D] text-sm font-bold text-white">
                    {index + 1}
                  </div>
                </div>
                <p className="text-base font-medium text-gray-700">{skill}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Impact Statement */}
      <section className="bg-gradient-to-b from-blue-50 to-white py-16">
        <div className="mx-auto max-w-5xl px-6">
          <div className="rounded-3xl bg-gradient-to-br from-[#457B9D] to-[#5a8fb0] p-8 shadow-2xl md:p-12">
            <div className="text-center text-white">
              <h2 className="mb-6 text-2xl font-bold md:text-3xl">
                Empowering the Next Generation
              </h2>
              <p className="mb-8 text-lg leading-relaxed text-white/95 md:text-xl">
                This 8-lesson curriculum uses a{" "}
                <span className="font-semibold">prevention-based education approach</span>{" "}
                designed to give young people the skills necessary to make smart choices and avoid risky behaviors that can hurt them physically, emotionally, socially, and spiritually.
              </p>
              <a
                href="/curriculum/order"
                className="group inline-flex items-center gap-2 rounded-full bg-white px-8 py-4 text-lg font-semibold text-[#457B9D] shadow-xl transition-all duration-300 hover:scale-105 hover:shadow-2xl"
              >
                Get Started Today
                <svg
                  className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M14 5l7 7m0 0l-7 7m7-7H3"
                  />
                </svg>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Youth Issues Section */}
      <YouthIssues />

      {/* Final CTA Section */}
      <section className="bg-gradient-to-r from-blue-50 to-pink-50 py-16">
        <div className="mx-auto max-w-4xl px-6 text-center">
          <h2 className="mb-6 text-3xl font-bold text-gray-800 md:text-4xl">
            Ready to Make a Difference?
          </h2>
          <p className="mb-8 text-xl text-gray-600">
            Invest in the future of young people in your community with Pathway Curriculum
          </p>
          <div className="flex flex-col gap-4 sm:flex-row sm:justify-center">
            <a
              href="/curriculum/order"
              className="rounded-xl bg-[#457B9D] px-8 py-4 text-lg font-semibold text-white shadow-lg transition-all duration-300 hover:bg-[#365f7a] hover:shadow-xl"
            >
              Order Curriculum
            </a>
            <a
              href="/contact"
              className="rounded-xl border-2 border-[#457B9D] bg-white px-8 py-4 text-lg font-semibold text-[#457B9D] shadow-lg transition-all duration-300 hover:bg-[#457B9D] hover:text-white hover:shadow-xl"
            >
              Learn More
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
